import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

import { assignClassLevel, removeClassLevel } from '../services/classAuditApi';
import { MAPPING_SOURCE, getClassStatus } from '../utils/classQualityChecks';
import { classNamePattern } from '../utils/classNamePattern';
import { levelOptionsForProgram, levelOptionLabel } from '../utils/levelOptions';
import RuleEditorDialog from './RuleEditorDialog';

// Issue-adaptive panel. It audits TWO values: the canonical level (SeasonManager-owned, editable)
// and the instructor (Dash-owned, read-only). The layout leads with the workflow the row's issue
// demands: an instructor-only issue leads with the Dash guidance; a missing level (with or without
// an instructor issue) leads with the level controls; a resolved row is a compact review. The level
// is ALWAYS editable regardless of instructor state -- ORDER adapts, availability never does (see
// the levelZone note). All LEVEL writes go through assignClassLevel / removeClassLevel and update
// the row in place from the resolved preview. Instructor data is strictly read-only -- it is fixed
// in Dash, never here.
export default function ClassLevelPanel({ row, levels, onRowUpdate, viewRows }) {
  const [selectedLevelId, setSelectedLevelId] = useState(row.effective_level_id ?? '');
  const [reason, setReason] = useState(row.override_reason ?? '');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // The rule-authoring seed for THIS class; non-null while the reusable-rule dialog is open.
  const [ruleDraft, setRuleDraft] = useState(null);
  // S5c: an ACKNOWLEDGEMENT of a publish that happened from this panel, so the outcome survives the
  // dialog closing. It is an EVENT RECORD, not a pending-state claim -- see publishedRule below.
  const [publishedRule, setPublishedRule] = useState(null);

  // Re-sync local edit state after the row changes (e.g. following a successful write).
  useEffect(() => {
    setSelectedLevelId(row.effective_level_id ?? '');
    setReason(row.override_reason ?? '');
    setErrorMsg('');
  }, [row.team_dim_id, row.effective_level_id, row.override_level_id, row.mapping_source, row.override_reason]);

  // The acknowledgement belongs to ONE class. A panel instance never sees a different class today
  // (ClassAuditList keys rows by team_dim_id and the row unmounts the panel when collapsed), but
  // clearing on identity change means a future keying change can't misattribute a publish. It is
  // deliberately NOT cleared by the effect above: assigning a level afterwards does not un-publish
  // the rule, so both facts stay true.
  useEffect(() => { setPublishedRule(null); }, [row.team_dim_id]);

  const mapping = row.mapping_source;
  const suggestedId = row.suggested_level_id ?? null;
  const currentEffectiveId = row.effective_level_id ?? null;

  // The season's program first, then the other program's (labelled): the class's level is not
  // constrained by its season's program. See levelOptionsForProgram.
  const availableLevels = levelOptionsForProgram(levels, row.class_program_type);
  const levelItems = availableLevels.map((l) => (
    <MenuItem key={l.level_id} value={l.level_id}>{levelOptionLabel(l)}</MenuItem>
  ));

  const hasSelection = selectedLevelId !== '' && selectedLevelId != null;
  // A "true override" = there IS a suggestion and the chosen level differs from it -> reason required.
  const isTrueOverride = suggestedId != null && hasSelection && selectedLevelId !== suggestedId;
  const reasonMissing = isTrueOverride && reason.trim() === '';
  const changed = hasSelection && selectedLevelId !== currentEffectiveId;
  const canSave = changed && !reasonMissing && !saving;
  const isRemovable = mapping === MAPPING_SOURCE.MANUAL_ASSIGNMENT || mapping === MAPPING_SOURCE.MANUAL_OVERRIDE;
  const primaryLabel =
    mapping === MAPPING_SOURCE.UNASSIGNED ? 'Assign'
      : mapping === MAPPING_SOURCE.AUTO_SUGGESTION ? 'Override'
        : 'Save';
  // Selecting the suggestion while editing an override reverts to automatic mapping.
  const willRevertToAuto =
    mapping === MAPPING_SOURCE.MANUAL_OVERRIDE && hasSelection && selectedLevelId === suggestedId;

  const runWrite = (promise) => {
    setSaving(true);
    setErrorMsg('');
    promise
      .then((preview) => { onRowUpdate(preview); setSaving(false); })
      .catch((err) => { setErrorMsg(err.message || 'Save failed. Please retry.'); setSaving(false); });
  };

  const handleSave = () => runWrite(assignClassLevel({
    team_dim_id: row.team_dim_id,
    level_id: selectedLevelId,
    override_reason: reason.trim() || undefined,
  }));

  const handleRemove = () => runWrite(removeClassLevel(row.team_dim_id));

  // ── reusable-rule seed for THIS class ──
  // The same shape groupMissingByStem emits, built for a single class so RuleEditorDialog needs no
  // changes. `occurrences` is deliberately ABSENT: nothing has been counted here, and asserting
  // "1 class in view" would read as "this is a one-off" -- which the loaded page cannot establish.
  //
  // The pattern seeds from classNamePattern() in BOTH cases, never from row.team_name_pattern. For
  // an auto-mapped class the matched pattern is the rule being outranked; seeding it would collide
  // with that active rule (publish refuses a duplicate scope + pattern) or simply reproduce it.
  //
  // S5b replaced classNameStem() here. The stem is a grouping key, not a matching pattern -- it
  // glues the edges after deleting mid-name schedule tokens, so it is frequently not a contiguous
  // substring and matched ZERO classes for 60% of the TY2026 unassigned population (measured
  // 2026-08-11). classNamePattern() guarantees a contiguous substring. It remains only a STARTING
  // SUGGESTION: the steward edits it, the preview is the authoritative reach check, and the server
  // still refuses a pattern that matches nothing. `group.stem` stays the field name because that is
  // RuleEditorDialog's existing seed contract -- the dialog is unchanged in this slice.
  //
  // Scope defaults to this class's franchise (franchiseCount 1 => the dialog unchecks "all
  // franchises"). That matters most for the more-specific-rule case: franchise-specificity is the
  // SECOND precedence criterion, so a franchise-scoped rule outranks the global rule it corrects
  // even at equal priority and pattern length. The level is never seeded -- choosing it is the point.
  const seedPattern = useMemo(() => classNamePattern(row.class_name), [row.class_name]);

  // ── recurrence evidence: a LOWER BOUND, never a measurement ──
  // Counts the loaded rows in the current view whose name literally contains this class's seed
  // pattern. Three reasons it can only ever be a floor:
  //   * the view is capped (5,000 rows) and filtered, so siblings outside it are invisible;
  //   * the count spans only the loaded tracking year, while a rule is not year-scoped;
  //   * plain substring containment is a SUBSET of production's LIKE, which honours '_' and '['.
  // Proven in production 2026-08-11: rule 163's candidate panel showed 4 visible siblings and the
  // rule actually reached 122 classes across 55 seasons and 11 tracking years -- 30x. So this may
  // only ever PROMOTE the rule path; the preview stays the authoritative reach check, and absence
  // of siblings is never rendered as evidence of anything (see the caption below).
  const recurrenceFloor = useMemo(() => {
    if (!seedPattern) return 0;
    const needle = seedPattern.toLowerCase();
    let n = 0;
    for (const other of viewRows || []) {
      if (other.team_dim_id === row.team_dim_id) continue;
      if (String(other.class_name || '').toLowerCase().includes(needle)) n += 1;
    }
    return n;
  }, [viewRows, seedPattern, row.team_dim_id]);

  const openRuleDraft = () => setRuleDraft({
    stem: seedPattern || row.class_name || '',
    programType: row.class_program_type || null,
    franchiseCount: 1,
    representativeFranchises: row.com_code ? [row.com_code] : [],
    variantCount: 1,
    sampleNames: row.class_name ? [row.class_name] : [],
  });

  // An automatic mapping the steward believes is wrong is corrected by a MORE SPECIFIC rule, not by
  // editing the one that matched (that pattern is probably right for the classes it maps). Every
  // other editable state is plain rule authoring.
  const ruleActionLabel = mapping === MAPPING_SOURCE.AUTO_SUGGESTION
    ? 'Create a more specific rule…'
    : 'Create reusable rule…';

  const status = getClassStatus(row);
  const instructorNotTracked = status.instructorNotTracked;
  const instructorNames = row.instructor_names || '';
  const instructorCount = row.instructor_count || 0;

  const KNOWN = [
    MAPPING_SOURCE.UNASSIGNED, MAPPING_SOURCE.AUTO_SUGGESTION,
    MAPPING_SOURCE.MANUAL_ASSIGNMENT, MAPPING_SOURCE.MANUAL_OVERRIDE,
  ];
  const isUnknownLevel = !KNOWN.includes(mapping);
  // ORDERING ONLY. Lead with the Dash instructor fix when the level is not itself missing
  // (instructor is the sole DETECTED issue). A missing level -- alone or alongside the instructor
  // issue -- leads with level controls.
  // This must NEVER gate whether the level can be edited: `status.levelMissing` is true only when
  // there is NO level, so treating it as "the level is fine" confuses *has a level* with *has the
  // RIGHT level*, and instructor tracking is an unrelated Dash-owned dimension either way.
  const instructorOnly = instructorNotTracked && !status.levelMissing && !isUnknownLevel;

  // ── evidence text, adapted to the current mapping state (shown with the editable level zone) ──
  let evidence;
  if (mapping === MAPPING_SOURCE.UNASSIGNED) {
    evidence = 'No automated suggestion for this class — assign a canonical level.';
  } else if (mapping === MAPPING_SOURCE.AUTO_SUGGESTION) {
    evidence = `Effective level: ${row.effective_level_name} — automatically mapped`
      + (row.team_name_pattern ? ` on “${row.team_name_pattern}”.` : '.');
  } else if (mapping === MAPPING_SOURCE.MANUAL_ASSIGNMENT) {
    evidence = `Effective level (manual assignment): ${row.effective_level_name}. No automated suggestion.`;
  } else if (mapping === MAPPING_SOURCE.MANUAL_OVERRIDE) {
    evidence = `Effective level (manual override): ${row.effective_level_name}. `
      + `Underlying automated suggestion: ${row.suggested_canonical_level_name || '—'}`
      + (row.team_name_pattern ? ` (matched on “${row.team_name_pattern}”).` : '.');
  }

  // ── AMBIGUITY DISCLOSURE ──
  // Two states, matching the operational rule: UNRESOLVED is loud (it is also what fails the nightly
  // warehouse integrity check); an ambiguity a manual override is holding settled is quiet
  // provenance, not an open issue.
  // COPY CONSTRAINT: the tied-rule details are not available to this panel yet, so nothing here may
  // imply it can name WHICH rules conflict -- only that equally-ranked rules disagree on the level.
  let ambiguityNote = null;
  if (status.ambiguityUnresolved) {
    ambiguityNote = (
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        This class matches two or more equally-ranked mapping rules that disagree on the level, so
        the level below was settled by a technical tie-break rather than by any rule being more
        specific. Assigning a canonical level here records the decision for this class.
      </Alert>
    );
  } else if (status.ambiguityResolvedByOverride) {
    ambiguityNote = (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Underlying rules are ambiguous; manual override is currently resolving this class.
      </Typography>
    );
  }

  // ── LEVEL ZONE ──
  // The level is editable in EVERY recognized mapping state. Instructor tracking is a separate,
  // Dash-owned dimension and deliberately has no branch here: an auto-suggested level can still be
  // WRONG, and a class whose instructor Dash can't identify is not thereby correctly classified.
  // The only non-editable case is an unrecognized mapping_source, where the control semantics
  // (Assign vs Override, reason-required, removable) are undefined.
  let levelZone;
  if (isUnknownLevel) {
    levelZone = (
      <Typography variant="body2" color="error">
        This class has an unrecognized level status and can’t be edited here.
      </Typography>
    );
  } else {
    // Full level controls: assignment (missing level / both) or review (resolved).
    levelZone = (
      <Box>
        {ambiguityNote}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{evidence}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }} disabled={saving}>
            <InputLabel id={`lvl-${row.team_dim_id}`}>Canonical level</InputLabel>
            <Select
              labelId={`lvl-${row.team_dim_id}`}
              label="Canonical level"
              value={selectedLevelId}
              onChange={(e) => setSelectedLevelId(e.target.value)}
            >
              {levelItems}
            </Select>
          </FormControl>

          {isTrueOverride && (
            <TextField
              size="small"
              label="Override reason (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={saving}
              error={reasonMissing}
              helperText={reasonMissing ? 'Required when overriding the automatic suggestion' : ''}
              sx={{ minWidth: 260 }}
            />
          )}

          <Button variant="contained" size="small" disabled={!canSave} onClick={handleSave}>
            {primaryLabel}
          </Button>

          {isRemovable && (
            <Button variant="outlined" size="small" color="inherit" disabled={saving} onClick={handleRemove}>
              {mapping === MAPPING_SOURCE.MANUAL_OVERRIDE ? 'Remove override' : 'Remove assignment'}
            </Button>
          )}

          {/* The rule path, reachable without leaving the class. Deliberately LOW EMPHASIS and in a
              FIXED position: the per-class action stays primary in every state, and recurrence
              evidence (S5b) will inform the choice without rearranging the controls. */}
          <Button variant="text" size="small" disabled={saving} onClick={openRuleDraft}>
            {ruleActionLabel}
          </Button>

          {saving && <CircularProgress size={20} sx={{ ml: 0.5, mt: 0.5 }} />}
        </Box>

        {willRevertToAuto && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Saving this reverts the class to its automatic suggestion.
          </Typography>
        )}

        {/* S5c publish acknowledgement. This is an EVENT RECORD -- "you published this rule" -- and
            deliberately NOT a pending-state claim. `pending_rebuild` is derived server-side and is
            re-derived on every fetch; caching it here would let a tab left open across a dbt build
            decay into a false "still pending" assertion. The authoritative current pending state is
            the page-level banner in ClassAudit, which refetches. So this line states only what
            happened, plus the one fact that stays true either way: the row below is still the
            warehouse's answer. Nothing about the class is mutated -- no onRowUpdate, no level
            change, no status change. */}
        {publishedRule && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Rule published: <b>{publishedRule.team_name_pattern}</b> →{' '}
            {publishedRule.canonical_level_name || `level #${publishedRule.level_id}`}
            {' '}({publishedRule.com_code === '*' ? 'all franchises' : publishedRule.com_code}).
            This class still shows its current warehouse-derived mapping; rule-driven mappings appear
            after the next warehouse build.
          </Alert>
        )}

        {/* Recurrence guidance. Rendered ONLY when siblings are actually present: silence when the
            count is 0 is the whole point -- a capped, filtered, single-year view cannot establish
            that a class is a one-off, so nothing here may say or imply "no recurrence". "At least"
            is load-bearing copy, not hedging. This is a full-width caption below the controls and
            deliberately does NOT reorder or re-emphasise the buttons above it. */}
        {recurrenceFloor > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            At least {recurrenceFloor} other {recurrenceFloor === 1 ? 'class' : 'classes'} in this
            view {recurrenceFloor === 1 ? 'shares' : 'share'} this naming pattern. A reusable rule
            may be a better fit.
          </Typography>
        )}

        {errorMsg && <Alert severity="error" sx={{ mt: 1 }}>{errorMsg}</Alert>}
      </Box>
    );
  }

  // ── INSTRUCTOR ZONE (read-only, Dash-owned) ──
  let instructorZone;
  if (instructorNotTracked) {
    instructorZone = (
      <Box>
        <Typography variant="subtitle2" sx={{ color: 'info.main' }}>
          Instructor not tracked — fix in Dash
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The warehouse can’t associate an instructor with this class from Dash event staffing, so
          instructor-based reporting is unavailable. The class may well have an instructor in
          practice — but Dash must identify the instructor on the class’s events for reporting to
          work. This is fixed in Dash, not SeasonManager.
        </Typography>
      </Box>
    );
  } else {
    instructorZone = (
      <Typography variant="body2" color="text.secondary">
        Instructor{instructorCount === 1 ? '' : 's'}: {instructorNames || '—'} ✓
      </Typography>
    );
  }

  // Order: an instructor-only issue leads with the Dash guidance, level (fine) below; otherwise the
  // level workflow leads and the instructor status follows.
  const zones = instructorOnly ? (
    <Box>
      {instructorZone}
      <Box sx={{ mt: 2 }}>{levelZone}</Box>
    </Box>
  ) : (
    <Box>
      {levelZone}
      <Box sx={{ mt: 2 }}>{instructorZone}</Box>
    </Box>
  );

  // A published rule is authoritative in Season Manager immediately but does not reach Class Audit
  // until the next warehouse build, so there is no row update to make here -- the dialog reports the
  // outcome itself. Retaining that outcome after the dialog closes is S5c.
  return (
    <>
      {zones}
      <RuleEditorDialog
        open={Boolean(ruleDraft)}
        mode="create"
        group={ruleDraft}
        levels={levels}
        trackingyear={row.trackingyear}
        onClose={() => setRuleDraft(null)}
        // Only the published rule is retained. The response also carries `preview` counts and
        // `warehouse_refresh_pending`, both deliberately dropped: the counts were a projection made
        // before the build, and the pending flag is exactly the value that must not be cached here.
        onSaved={(res) => { if (res && res.rule) setPublishedRule(res.rule); }}
      />
    </>
  );
}
